/**
 * The left column, and now the whole of the app chrome.
 *
 * SHAPED LIKE SALESFORGE'S, IN OUR WORDS. A 280px column on the muted ground
 * with a 15px inset, so every band stands on the same 250px of content, in
 * three parts: the product's row at the top, one scrolling region holding the
 * four labelled groups of rows with the sending allowance card at their end,
 * and a footer of Shortcuts, Settings and Handbook, the workspace switcher
 * (the brand, and the every-brand view for the people allowed it), and the
 * person at the bottom. Folded, it becomes the 64px
 * rail in `CollapsedRail.tsx`, drawn from the same lists.
 *
 * THERE IS NO TOP BAR. Everything that used to run across the top of the
 * window is in this column, and `stacking.test.ts` holds each piece to the
 * band it landed in.
 *
 * The bands are separate files because they are separate concerns with their
 * own state: `SidebarWorkspace` owns the product row at the top and, in the
 * footer, `WorkspaceSwitcher`, the brand menu with the every-brand view and the
 * workspace window, and `SidebarAccount` owns the account menu and the two
 * dialogs behind it. What is left in this file is the
 * lists themselves, which the command palette also reads, and the rows.
 */
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Suspense, lazy, type ComponentType } from 'react'
import { cn } from '../lib/cn'
import { formatNumber } from '../lib/format'
import { useSession } from '../lib/session'
import { useShortcuts } from '../pages/queue/keyboard'
import { useMyLimits } from '../pages/settings/myLimitsApi'
import { Badge } from './Badge'
import { LinkButton } from './Button'
import { CollapsedRail, RailButton, RailLink } from './CollapsedRail'
import { MiniMeter } from './Meter'
import { ShortcutsKeyCaps, useOpenShortcuts } from './ShortcutsDrawer'
import { SidebarAccount } from './SidebarAccount'
import { SidebarWorkspace, WorkspaceSwitcher } from './SidebarWorkspace'
import { useNavCounts } from './nav-counts'
import { IconTarget, IconTrend } from '../pages/prospecting/shared/icons'
import { IconCompanyList, IconScoring, IconSignals } from '../pages/discovery/icons'
import { IconAiArk } from '../pages/discovery/ai-ark/icons'
import { useProspectingSeat } from '../pages/prospecting/shared/useProspectingSeat'
import { useOperatorReach } from '../pages/operator/shared/useOperatorMe'
import {
  OPERATOR_HEADING,
  PROSPECTING_HEADING,
  anyOperatorLit,
  anyProspectingLit,
  groupsAroundProspecting,
  navBadge,
  operatorLinkLit,
  operatorLinksFor,
  operatorTaskLinksFor,
  prospectingChildLit,
  prospectingLinkLit,
  prospectingLinksFor,
  rowEnd,
  sendingAllowance,
  settingsRowLit,
  visibleGroups,
  type NavBadge,
  type NavBadgeKey,
  type NavCounts,
  type OperatorGlyph,
  type ProspectingGlyph,
  type RenderedGroup,
} from './sidebar-model'
import type { IconProps } from './icons'
import {
  IconAccounts,
  IconAnalytics,
  IconApprovals,
  IconBrands,
  IconCalendar,
  IconCalendarCheck,
  IconCampaigns,
  IconCheck,
  IconClock,
  IconEdit,
  IconFile,
  IconInbox,
  IconKey,
  IconKnowledge,
  IconMailbox,
  IconPeople,
  IconPolicy,
  IconProspects,
  IconQuestion,
  IconQueue,
  IconSearch,
  IconSettings,
  IconShield,
  IconSparkle,
  IconUploadCloud,
  IconUser,
  IconUserSparkle,
  IconVideo,
} from './icons'

/** The bot's live state beside the Operator heading, loaded with the Operator pages rather than with the shell. */
const BotStateIndicator = lazy(() => import('../pages/operator/shared/BotStateIndicator').then((m) => ({ default: m.BotStateIndicator })))

/**
 * The glyph per Prospecting row, the ones the old app's column drew. The two
 * the shared set lacks, the target and the trend line, are the prospects
 * pages' own (pages/prospecting/shared/icons.tsx).
 *
 * Review is the one that differs. The old column drew two ticks there and the
 * shared set has one, so the row wears the single tick from `icons.tsx`. It is
 * not the boxes of the Queue row, nor the ticked clipboard of Approvals.
 */
export const PROSPECTING_GLYPHS: Record<ProspectingGlyph, ComponentType<IconProps>> = {
  review: IconCheck,
  queue: IconQueue,
  posts: IconEdit,
  audience: IconPeople,
  prospects: IconTarget,
  trends: IconTrend,
  results: IconAnalytics,
  keywords: IconSearch,
}

/**
 * The glyph per Operator row, all from the shared set. Live is the camera the
 * Operator pages' own frame wears, so the rail's one glyph is that camera too.
 */
export const OPERATOR_GLYPHS: Record<OperatorGlyph, ComponentType<IconProps>> = {
  live: IconVideo,
  meetings: IconCalendar,
  ask: IconQuestion,
  insights: IconAnalytics,
  talk: IconUserSparkle,
  clone: IconUser,
  preferences: IconSettings,
  onboarding: IconSparkle,
  sandbox: IconBrands,
  settings: IconSettings,
  companies: IconCalendarCheck,
}

/**
 * The handset, drawn here rather than in the icon set.
 *
 * `icons.tsx` is shared by every screen and is being edited elsewhere while
 * this entry is added, so the one glyph that only this nav item needs is kept
 * local rather than racing another change into a file nobody owns. It is drawn
 * on the same 24 grid at the same stroke weight as the rest, so it sits level
 * with its neighbours in the list.
 */
/**
 * Being told about something, drawn here for the same reason as the handset
 * below: `icons.tsx` is shared and is being edited elsewhere, and this glyph
 * has one caller. Same 24 grid and same stroke weight as the rest of the set.
 */
function IconAlert({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
      <path d="M10.5 18.5a2 2 0 0 0 3 0" />
    </svg>
  )
}

function IconDevice({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M10.5 6h3M11 17.5h2" />
    </svg>
  )
}

/**
 * A keyboard, for the Shortcuts link at the foot of the column.
 *
 * Local for the same reason the three glyphs above are local: `icons.tsx` is
 * shared by every screen and is edited from several directions at once, and
 * this one has a single caller. Same 24 grid, same stroke weight.
 */
function IconKeyboard({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <path d="M6.5 9.5h.01M10 9.5h.01M13.5 9.5h.01M17 9.5h.01M8 14.5h8" />
    </svg>
  )
}

/**
 * Angle brackets, for the API reference.
 *
 * Local for the same reason `IconDevice` above is local: `icons.tsx` is shared
 * by every screen and is being edited elsewhere while this entry is added, so
 * the one glyph only this nav item needs is drawn here rather than racing
 * another change into a file nobody owns. Same 24 grid, same stroke weight, so
 * it sits level with its neighbours.
 */
function IconCode({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      <path d="m8.5 8.5-4 3.5 4 3.5" />
      <path d="m15.5 8.5 4 3.5-4 3.5" />
      <path d="m13.5 5-3 14" />
    </svg>
  )
}

export interface NavItem {
  to: string
  label: string
  icon: ComponentType<IconProps>
  end?: boolean
  /**
   * Paths under this row's own that still light it while `end` is set,
   * because they are reached from it. Company lists is `/discovery` exactly,
   * so it stays dark on Signals and Scoring beside it, and one list at
   * `/discovery/lists/<id>` is still the Company lists row's page. See
   * `rowEnd` in the model.
   */
  also?: readonly string[]
  adminOnly?: boolean
  /**
   * Offered only to an Operator admin (`useOperatorReach().admin`, the
   * service's own role), as the Settings tab strip offers the Operator tab
   * (`tabsFor`). This app's admin role is not the question.
   */
  operator?: boolean
  /** Shown only in the super admin view: a super admin with the switch on. */
  superAdminView?: boolean
  /** One line explaining the page, shown as the link title. */
  hint?: string
  /**
   * Which live count this row wears, if any.
   *
   * A key and not a number, because this list is a module constant read at
   * import time and the counts arrive from the server long after. The row says
   * which number it wants and the sidebar looks it up. A zero and a count that
   * has not landed yet both draw nothing: see `navBadge`.
   */
  badge?: NavBadgeKey
  /**
   * A second count drawn in front of `badge`, as Salesforge's Primebox row
   * wears its action-required count in a filled circle before the unread one.
   */
  leadBadge?: NavBadgeKey
  /**
   * A short word in a warning pill, for a destination that is new enough that
   * somebody who knows this nav would otherwise walk straight past it.
   *
   * Deliberately a free string rather than a boolean, so it says what it means
   * where it is set, and deliberately not dated: the pill comes off when the
   * thing stops being new, which is a decision a person makes.
   */
  flag?: string
}

export interface NavGroup {
  heading: string
  items: NavItem[]
}

/*
 * SALESFORGE'S THREE GROUPS AND ITS SIX ROWS, IN ITS ORDER AND ITS WORDS.
 *
 * Threads holds the inbox (their Primebox). Outreach holds Approvals in the
 * slot where Salesforge has AI Agent HQ, because the queue of drafts a person
 * signs off is our AI agent's desk, then Sequences. Database & Infrastructure
 * holds Senders & Mailboxes, Contacts and Knowledge (their Forge AI).
 *
 * Everything that used to be a row of its own now lives inside one of these,
 * as Salesforge keeps its own extras: Analytics and Workflows are tabs on
 * Sequences, Luma events and Sales Navigator are sources in Add contacts, the
 * enrichment screens are a tab on Contacts, the phones are a tab on Senders &
 * Mailboxes, and the activity log is a tab in Settings. The dashboard is gone:
 * signing in lands on the inbox, as Salesforge lands on Primebox.
 *
 * DISCOVERY IS THE SECOND GROUP, above Outreach. The owner asked for it on
 * 2026-10-26: a module "specifically for DISCOVERING and RESEARCHING which
 * companies would be ideal prospects for us", drawn "higher than Outreach in
 * the left hand menu", for every brand rather than one. Finding the companies
 * comes before writing to them, so the column reads Threads, Discovery,
 * Outreach, Prospecting, then Database & Infrastructure. Admin only for now,
 * like the paid checks it can start (docs/DISCOVERY.md).
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    heading: 'Threads',
    items: [
      {
        to: '/inbox',
        label: 'Inbox',
        icon: IconInbox,
        leadBadge: 'inboxAction',
        badge: 'inboxUnread',
        hint: 'Every reply across channels',
      },
    ],
  },
  {
    heading: 'Discovery',
    items: [
      {
        to: '/discovery',
        label: 'Company lists',
        icon: IconCompanyList,
        end: true,
        also: ['/discovery/lists'],
        adminOnly: true,
        hint: 'Upload companies and see which ones show money and a reason to buy',
      },
      {
        to: '/discovery/signals',
        label: 'Signals',
        icon: IconSignals,
        adminOnly: true,
        hint: 'The 12 signals, where each one comes from and what it costs',
      },
      {
        to: '/discovery/scoring',
        label: 'Scoring',
        icon: IconScoring,
        adminOnly: true,
        hint: 'Which signals count for each campaign and how much',
      },
      {
        // AI Ark (docs/AI-ARK-HANDOVER.md, Fahad's ask of 2026-10-31): search
        // AI Ark's people and companies and add the people to a Contacts list.
        // Admin only like the rows above, since adding people spends credits.
        // No `end`, so its Exports page keeps it lit.
        to: '/discovery/ai-ark',
        label: 'AI Ark',
        icon: IconAiArk,
        adminOnly: true,
        hint: 'Search AI Ark for people and companies and add them to a list in Contacts',
      },
    ],
  },
  {
    heading: 'Outreach',
    items: [
      {
        to: '/approvals',
        label: 'Approvals',
        icon: IconApprovals,
        badge: 'approvals',
        hint: 'Drafts waiting for you, and the messages that did not send',
      },
      { to: '/playbooks', label: 'Sequences', icon: IconCampaigns, hint: 'Every sequence, its steps, people and results' },
      { to: '/publish', label: 'Publish', icon: IconUploadCloud, hint: "Each brand's posts, ideas and videos, written once and sent to every channel" },
    ],
  },
  {
    heading: 'Database & Infrastructure',
    items: [
      {
        to: '/senders',
        label: 'Senders & Mailboxes',
        icon: IconAccounts,
        hint: 'Sending accounts, mailboxes and the phones they are on',
      },
      { to: '/prospects', label: 'Contacts', icon: IconProspects, hint: 'Everybody you can reach, and where they came from' },
      { to: '/knowledge', label: 'Knowledge', icon: IconFile, hint: 'Offers and the brand file the writer works from' },
    ],
  },
]

/**
 * The three screens that read every brand at once.
 *
 * They were rows in the list above that appeared when the super admin view was
 * on. They live behind the view switcher now, which is where Salesforge keeps
 * its own switch between products, and the command palette still offers them
 * to the same people on the same terms.
 */
export const PLATFORM_ITEMS: NavItem[] = [
  {
    to: '/admin/brands',
    label: 'All brands',
    icon: IconBrands,
    superAdminView: true,
    hint: 'Every brand at once: readiness and what is working',
  },
  {
    to: '/admin/campaigns',
    label: 'All sequences',
    icon: IconCampaigns,
    superAdminView: true,
    hint: 'Every sequence in every brand, whoever built it',
  },
  {
    to: '/admin/prospects',
    label: 'All people',
    icon: IconProspects,
    superAdminView: true,
    hint: 'Every person in every brand',
  },
]

/**
 * The Handbook, the third row in the utility band.
 *
 * Salesforge puts its Academy here. Ours is the one document that explains the
 * platform, at `/handbook` since it came out of settings, which is also where
 * every help link in the console points.
 */
export const HANDBOOK_ROW: NavItem = {
  to: '/handbook',
  label: 'Handbook',
  icon: IconKnowledge,
  hint: 'What this platform is and how to drive it, in one document',
}

/**
 * Every row in this column that points inside `/settings`, so the Settings row
 * can stay dark on a screen that already has a row of its own.
 */
export const OWN_ROWS_IN_SETTINGS: readonly NavItem[] = [
  ...NAV_GROUPS.flatMap((group) => group.items),
  HANDBOOK_ROW,
].filter((item) => item.to.startsWith('/settings/'))

/**
 * CONFIGURATION IS ONE DESTINATION NOW, AND THIS IS WHAT IS BEHIND IT.
 *
 * These thirteen screens used to be a "Setup" group in the list above, folded
 * behind a disclosure because they were more than half of the whole navigation
 * and left open they made the sidebar taller than the window. A fold was the
 * wrong answer to that. It cost a click, it hid the thing it was hiding badly,
 * and it left the nav answering two different questions at once: where do I
 * work, and where do I configure.
 *
 * The nav above answers the first. `/settings` answers the second, with these
 * in a rail down its left side, and the sidebar carries ONE row pointing at it.
 * That is the reference's shape too: a single Settings link in the utility band
 * at the foot of the column.
 *
 * WHY THIS IS STILL A LIST OF THE SAME SHAPE. Three readers need it and they
 * would otherwise each hold their own copy: the rail draws it, the overview
 * page draws a card per entry off the same hints, and the command palette
 * offers every one of them so that `mod+k` still reaches a settings screen in
 * one step. A list nobody can fork is what keeps those three honest.
 */
export const SETTINGS_GROUPS: NavGroup[] = [
  {
    heading: 'Sending',
    items: [
      // Channel accounts left this list for its own row in the sidebar, as
      // Senders and mailboxes. Listing it here too would be a second way to a
      // screen that is one click away already, under a different name.
      {
        // ONE ENTRY WHERE THERE WERE TWO. This was "My sending limits" and there
        // was an admin-only "Limits and safety" beside it, so somebody looking
        // for a number had to know which held it, and the answer depended on
        // whether they were an admin. The page gates its own tabs now, so this
        // is not admin only: a member lands on their own allowance and simply
        // is not shown the three tabs they cannot change.
        to: '/settings/safety',
        label: 'Safety controls',
        icon: IconPolicy,
        hint: 'What you may send, and every limit the platform holds everybody to',
      },
      { to: '/settings/suppressions', label: 'Suppressions', icon: IconShield, hint: 'Who must not be contacted, and why' },
      { to: '/settings/mailboxes', label: 'Mailboxes', icon: IconMailbox, hint: 'Mailboxes scanned for prior conversations' },
    ],
  },
  {
    heading: 'Brands',
    items: [
      { to: '/settings/brands', label: 'Brands', icon: IconBrands, hint: 'Brands and who belongs to them' },
      // Offers is not on this list. It lives under Knowledge in the sidebar
      // with the brand file, and `/settings/offers` redirects there.
      {
        to: '/settings/lead-magnets',
        label: 'Lead magnets',
        icon: IconKnowledge,
        hint: 'Guides and checklists you give away, and who took them',
      },
      {
        to: '/settings/variables',
        label: 'Variables',
        icon: IconCode,
        hint: 'What you can put in a message, and how many people have it',
      },
    ],
  },
  {
    heading: 'The platform',
    items: [
      // The Handbook left for `/handbook` and the row at the foot of the sidebar.
      {
        to: '/settings/api',
        label: 'API reference',
        icon: IconCode,
        hint: 'The endpoints an API key can call, and the keys themselves',
      },
      {
        to: '/settings/activity',
        label: 'Activity log',
        icon: IconClock,
        hint: 'What has been done here, and what was deleted',
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/settings/people', label: 'People', icon: IconPeople, adminOnly: true, hint: 'Accounts, access and brand membership' },
      {
        to: '/settings/integrations',
        label: 'Alert destinations',
        icon: IconAlert,
        adminOnly: true,
        hint: 'Who is told when something needs a person, by webhook or by email',
      },
      {
        to: '/settings/device',
        label: 'Device',
        icon: IconDevice,
        adminOnly: true,
        hint: 'The phone, and what the engine says it performs',
      },
      { to: '/settings/engine', label: 'Device engine', icon: IconKey, adminOnly: true, hint: 'Engine tokens and whether the phone is checking in' },
      {
        // The prospects pages' settings, a tab of the Settings page, which were
        // a row called Settings in the Prospecting group until 2026-10-05.
        // Listed here for the palette. A member with a seat finds them under
        // the Prospecting entries the palette offers them (CommandPalette.tsx).
        to: '/settings/prospecting',
        label: 'Prospecting',
        icon: IconTarget,
        adminOnly: true,
        hint: 'Reply drafts, people and brands, the writing schedule and everything the searches find',
      },
      {
        // Operator's settings, a tab of the Settings page. Listed here for the
        // palette, and offered only to an Operator admin (`operator`), like the
        // tab: an admin here with no admin role there would land on a page
        // that only says admins only. A member here who is an Operator admin
        // finds them under the Operator entries (CommandPalette.tsx).
        to: '/settings/operator',
        label: 'Operator',
        icon: IconVideo,
        adminOnly: true,
        operator: true,
        hint: 'The clone and its prompts, models, voice and face, the meetings it joins, and who may use it',
      },
      {
        // Operator > Companies' registry, schedule, budget, sources and rules,
        // a tab of the Settings page. Listed here for the palette.
        to: '/settings/operator-companies',
        label: 'Operator companies',
        icon: IconCalendarCheck,
        adminOnly: true,
        hint: 'The companies and people the Operator works for, when it runs, what it may spend and its rules',
      },
      {
        // Discovery's sources, keys, n8n webhooks and limits, a tab of the
        // Settings page. Listed here for the palette, admin only like the rows.
        to: '/settings/discovery',
        label: 'Discovery',
        icon: IconSignals,
        adminOnly: true,
        hint: 'Which sources Discovery may use, the keys they need, your n8n webhooks and the limits',
      },
      {
        // AI Ark's prices, limits, balance floor and switches, and the spend, a
        // tab of the Settings page. Listed here for the palette, admin only.
        to: '/settings/ai-ark',
        label: 'AI Ark',
        icon: IconAiArk,
        adminOnly: true,
        hint: 'What AI Ark may spend per run and per day, the balance floor, the switches and the spend so far',
      },
    ],
  },
]

/*
 * THREE THINGS IN THE DESIGN ARE DELIBERATELY NOT IN THIS LIST.
 *
 * A row of small round icons and an "App" row with the two store glyphs. There
 * is no consumer mobile app in this product and there is no plan for one, so
 * both would be buttons pointing at nothing. A control that does nothing is
 * worse than an absence: somebody presses it, and after that they trust the
 * rest of the column a little less.
 *
 * An integration switcher, drawn in the design as a Salesforce row with an
 * up/down chevron. This platform talks to Instantly, BetterEnrich,
 * CatchAllVerifier, the news API and Luma, and to no CRM at all, so there is
 * nothing for it to switch BETWEEN. Naming a product this platform does not
 * integrate with would be a claim on the navigation itself, which is the last
 * place a reader expects to be misled. The nearest real thing, where alerts are
 * sent, is already a Setup entry under its own name.
 */

/*
 * THE ROW, measured off the reference: an anchor 36px tall with 8px of padding,
 * 8px corners, 12px between a 20px glyph box and a 14px medium label.
 *
 * A grey fill marks the row you are on rather than a violet one. The accent is
 * spent on the single filled button in a page header and on the active tab
 * underline, and a third violet block in the corner of every screen made those
 * two compete rather than lead. The rows sit on the muted ground, so hover is
 * the stronger grey that still reads against it.
 */
const ROW = 'flex h-9 w-full items-center gap-3 rounded-lg p-2 text-base font-medium transition'
const ROW_ACTIVE = 'bg-surface-selected text-ink-secondary'
const ROW_IDLE = 'text-ink-muted hover:bg-surface-hover-strong hover:text-ink-secondary'

export function Sidebar({
  collapsed = false,
  onNavigate,
  onToggleCollapsed,
  onClose,
  className,
}: {
  collapsed?: boolean
  onNavigate?: () => void
  /**
   * Fold the column to a rail. Absent in the slide-over, which has nothing to
   * fold into, and that absence is what hides the control there rather than a
   * second flag saying so.
   */
  onToggleCollapsed?: () => void
  /** Present only in the slide-over, where the same slot closes it. */
  onClose?: () => void
  className?: string
}) {
  const { isAdmin, superAdminView } = useSession()
  const { pathname } = useLocation()
  const counts = useNavCounts()
  const openShortcuts = useOpenShortcuts()
  const groups = visibleGroups(NAV_GROUPS, isAdmin, superAdminView)
  // Admins, and anybody the prospects service says has a seat.
  const prospecting = prospectingLinksFor(isAdmin, useProspectingSeat())
  const prospectingLit = anyProspectingLit(pathname)
  // The same rule for Operator: admins, and anybody the Operator service says
  // has a seat, with Talk only for whom it may open. No count, so nothing here
  // waits on that service.
  const operatorReach = useOperatorReach()
  const operator = operatorLinksFor(operatorReach)
  // The column draws the task engine's rows (Companies) after them, for admins.
  const operatorRows = [...operator, ...operatorTaskLinksFor(isAdmin)]
  const operatorLit = anyOperatorLit(pathname)
  // Threads, Discovery and Outreach, then the Prospecting rows, then Database
  // & Infrastructure. Both the open column and the rail draw it in this order.
  const { above, below } = groupsAroundProspecting(groups)
  const settingsLit = settingsRowLit(pathname, OWN_ROWS_IN_SETTINGS)

  /*
   * The fold, on a key as well as on the icon button.
   *
   * Bound here rather than beside the button because this component is the one
   * that is handed the toggle, and it is bound OFF in the slide-over: there is
   * no column to fold there, and with two sidebars mounted at once the same key
   * would otherwise be claimed twice and the second binding would win.
   *
   * The key itself is not written here. It comes from the registry row the
   * shortcuts drawer draws its cap from, which is what stops the caption and
   * the binding from disagreeing.
   */
  useShortcuts(
    'sidebar',
    { 'sidebar.collapse': () => onToggleCollapsed?.() },
    { enabled: onToggleCollapsed !== undefined },
  )

  if (collapsed) {
    /*
      THE RAIL, measured: 64px, the same muted ground, items centred. Expand
      first, then the brand, the nav glyphs, the utility glyphs, the view
      switcher's mark, the account kebab and the avatar. The brand mark is ours
      rather than the reference's: the brand is the one thing an operator must
      never have to unfold the column to check.
    */
    return (
      <div className={cn('flex h-full min-h-0 flex-col items-center py-3', className)}>
        <SidebarWorkspace collapsed onToggleCollapsed={onToggleCollapsed} onClose={onClose} />
        <CollapsedRail
          groups={above}
          between={
            // Prospecting as one glyph, where the open column draws its rows.
            // It opens the first row, the Queue, and is lit on any of them.
            prospecting[0] === undefined ? undefined : (
              <RailLink
                to={prospecting[0].href}
                label={PROSPECTING_HEADING}
                icon={IconTarget}
                active={prospectingLit}
                badge={navBadge(prospecting[0].badge, counts)}
                onNavigate={onNavigate}
              />
            )
          }
          betweenNext={
            // Operator as one glyph, after Prospecting, as the open column
            // draws its rows. It opens the first row, Live, and is lit on any
            // Operator page.
            operator[0] === undefined ? undefined : (
              <RailLink
                to={operator[0].href}
                label={OPERATOR_HEADING}
                icon={IconVideo}
                active={operatorLit}
                onNavigate={onNavigate}
              />
            )
          }
          groupsAfter={below}
          counts={counts}
          onNavigate={onNavigate}
        >
          <RailButton
            label="Shortcuts"
            icon={IconKeyboard}
            onClick={() => openShortcuts?.()}
            disabled={openShortcuts === null}
            caps={<ShortcutsKeyCaps />}
          />
          <RailLink to="/settings" label="Settings" icon={IconSettings} active={settingsLit} onNavigate={onNavigate} />
          <RailLink to={HANDBOOK_ROW.to} label={HANDBOOK_ROW.label} icon={HANDBOOK_ROW.icon} onNavigate={onNavigate} />
        </CollapsedRail>
        <WorkspaceSwitcher collapsed ownsModal={onClose === undefined} platformItems={PLATFORM_ITEMS} />
        <SidebarAccount collapsed />
      </div>
    )
  }

  return (
    /*
      THREE PARTS, AND THE MIDDLE ONE SCROLLS.

      The workspace row at the top and the footer at the bottom are fixed to
      the ends of the column, because they answer the two questions somebody
      asks on arrival, which brand and who am I, and the ways to settings and
      the keyboard that must never scroll away. Everything between them is ONE
      scrolling region: the four groups and, at their end, the allowance card.

      That is the reference's shape, and it is a fix rather than a preference.
      With the card, the utilities, the view switcher and the person all pinned
      under the list, and a product mark and a search face pinned over it, the
      fixed parts took most of a 796px window and the groups were left a
      scroll area about 150px tall. The card is the tallest thing in the column
      and the one nobody needs on every screen, so it scrolls with the list.

      The 15px inset is on the column rather than on each part, so everything
      stands on the same 250px of content and the rows line up with the brand
      name above them without each part doing its own sum. The scroll region
      takes the inset back and pads it in again so its scrollbar sits at the
      column's edge rather than over the rows.
    */
    <div className={cn('flex h-full min-h-0 flex-col px-[0.9375rem]', className)}>
      <SidebarWorkspace onToggleCollapsed={onToggleCollapsed} onClose={onClose} />

      <div data-sidebar-scroll className="-mx-[0.9375rem] min-h-0 flex-1 overflow-y-auto px-[0.9375rem] pb-4">
        <nav aria-label="Main">
          {/*
            THE GROUPS ARE DRAWN IN TWO RUNS, WITH PROSPECTING BETWEEN THEM.
            Threads, Discovery and Outreach, then the Prospecting rows, then
            Database & Infrastructure: the work first and what it stands on
            last, which is the order the owner asked for (2026-10-05, and
            Discovery above Outreach on 2026-10-26). `NAV_GROUPS` is not
            reordered for it, see `groupsAroundProspecting` in the model.
          */}
          {above.map((group) => (
            <NavGroupBlock key={group.heading} group={group} counts={counts} onNavigate={onNavigate} />
          ))}

          {/*
            PROSPECTING, the prospects pages (see PROSPECTING_LINKS in the
            model). Router links like every other row, so moving between them
            loads nothing, lit by the path this router is on. A row with pages
            under it draws them beneath it while one of them is open. Same row,
            same heading, same glyph size as the groups around it.
          */}
          {prospecting.length === 0 ? null : (
            <div className="mt-5">
              <p className="mb-1 flex h-[1.125rem] items-center pl-2 text-sm font-semibold text-ink-subtle">{PROSPECTING_HEADING}</p>
              <ul className="space-y-0.5">
                {prospecting.map((link) => {
                  const Glyph = PROSPECTING_GLYPHS[link.glyph]
                  const lit = prospectingLinkLit(link, pathname)
                  const badge = link.badge === undefined ? null : navBadge(link.badge, counts)
                  return (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        onClick={onNavigate}
                        title={link.hint}
                        aria-current={lit && link.children === undefined ? 'page' : undefined}
                        className={cn(ROW, lit ? 'bg-surface-selected text-ink-secondary hover:bg-surface-hover-strong' : ROW_IDLE)}
                      >
                        <Glyph className={cn('h-[1.25rem] w-[1.25rem]', lit ? 'text-ink-secondary' : 'text-ink-subtle')} />
                        <span className="truncate">{link.label}</span>
                        {badge === null ? null : <NavCount badge={badge} className="ml-auto" />}
                      </Link>
                      {lit && link.children !== undefined ? (
                        <ul className="mt-0.5 space-y-0.5 pl-9">
                          {link.children.map((child) => {
                            const on = prospectingChildLit(child.href, pathname)
                            return (
                              <li key={child.href}>
                                <Link
                                  to={child.href}
                                  onClick={onNavigate}
                                  aria-current={on ? 'page' : undefined}
                                  className={cn(
                                    'flex h-8 w-full items-center rounded-lg px-2 text-sm font-medium transition',
                                    on ? 'bg-surface-selected text-ink-secondary' : ROW_IDLE,
                                  )}
                                >
                                  <span className="truncate">{child.label}</span>
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/*
            OPERATOR, what was the Son of Basel portal (see OPERATOR_LINKS in
            the model). After Prospecting, before Database & Infrastructure.
            Router links, the same row and heading as the Prospecting block,
            and no counts. The task engine's rows (OPERATOR_TASK_LINKS,
            Companies) come last, for admins only (operatorTaskLinksFor).
          */}
          {operatorRows.length === 0 ? null : (
            <div className="mt-5">
              <p className="mb-1 flex h-[1.125rem] items-center justify-between gap-2 pl-2 pr-2 text-sm font-semibold text-ink-subtle">
                <span>{OPERATOR_HEADING}</span>
                {/*
                  What the bot is doing, as the old portal's top bar showed on
                  every page. Only while an Operator page is open, and only for
                  somebody with a seat, so no other screen polls the service.
                  Lazy, so its code loads with the Operator pages.
                */}
                {operatorLit && operatorReach.seat ? (
                  <Suspense fallback={null}>
                    <BotStateIndicator />
                  </Suspense>
                ) : null}
              </p>
              <ul className="space-y-0.5">
                {operatorRows.map((row) => {
                  const Glyph = OPERATOR_GLYPHS[row.glyph]
                  const lit = operatorLinkLit(row, pathname)
                  return (
                    <li key={row.href}>
                      <Link
                        to={row.href}
                        onClick={onNavigate}
                        title={row.hint}
                        aria-current={lit ? 'page' : undefined}
                        className={cn(ROW, lit ? 'bg-surface-selected text-ink-secondary hover:bg-surface-hover-strong' : ROW_IDLE)}
                      >
                        <Glyph className={cn('h-[1.25rem] w-[1.25rem]', lit ? 'text-ink-secondary' : 'text-ink-subtle')} />
                        <span className="truncate">{row.label}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {below.map((group) => (
            <NavGroupBlock key={group.heading} group={group} counts={counts} onNavigate={onNavigate} />
          ))}
        </nav>

        <SendingAllowanceCard onNavigate={onNavigate} />
      </div>

      <div data-sidebar-footer className="shrink-0 pb-4 pt-2">
        {/*
          THE UTILITY BAND: Shortcuts, Settings and Handbook, the reference's
          three in the reference's order, drawn as ordinary rows.

          The cap is printed beside Shortcuts because that is the whole point of
          a shortcuts panel: the person most likely to click it is the person
          who has not yet learned there is a key that does the same thing. It is
          read off the registry row the key is bound from rather than typed
          here, so the two cannot come apart.
        */}
        <ul className="space-y-0.5">
          <li>
            <button
              type="button"
              onClick={() => openShortcuts?.()}
              disabled={openShortcuts === null}
              className={cn(ROW, ROW_IDLE, 'disabled:opacity-60')}
            >
              <IconKeyboard className="h-[1.25rem] w-[1.25rem] text-ink-subtle" />
              <span className="truncate">Shortcuts</span>
              <ShortcutsKeyCaps className="ml-auto" />
            </button>
          </li>
          <li>
            {/*
              A plain link with its own idea of "here", rather than a NavLink.
              Every configuration screen lives under this path, so a NavLink
              would light this row on Device and on the Handbook too, beside
              the rows named for them.
            */}
            <Link
              to="/settings"
              onClick={onNavigate}
              title="Everything you configure, in one place"
              aria-current={settingsLit ? 'page' : undefined}
              className={cn(ROW, settingsLit ? ROW_ACTIVE : ROW_IDLE)}
            >
              <IconSettings className={cn('h-[1.25rem] w-[1.25rem]', settingsLit ? 'text-ink-secondary' : 'text-ink-subtle')} />
              <span className="truncate">Settings</span>
            </Link>
          </li>
          <li>
            <NavRow item={HANDBOOK_ROW} badge={null} onNavigate={onNavigate} />
          </li>
        </ul>

        <WorkspaceSwitcher ownsModal={onClose === undefined} platformItems={PLATFORM_ITEMS} />
        <SidebarAccount />
      </div>
    </div>
  )
}

/**
 * One labelled group of the open column: its heading, then its rows.
 *
 * A component of its own since the column began drawing its groups in two
 * runs, with the Prospecting rows between them. Two copies of this markup
 * would drift apart, and `sidebar-model.test.ts` measures the heading's
 * classes off this file, where it can only measure one of them.
 */
function NavGroupBlock({
  group,
  counts,
  onNavigate,
}: {
  group: RenderedGroup<NavItem>
  counts: NavCounts
  onNavigate?: () => void
}) {
  return (
    <div className="mt-5">
      {/*
        A WORD, NOT A STAMP. 13/18 semibold in sentence case, one weight
        up from the rows and one step lighter in colour, which reads as a
        heading at a glance. "Database and infrastructure" set in caps
        with wide tracking was a wall. It also names the list for a
        screen reader, and the command palette groups its results by it.
      */}
      <p className="mb-1 flex h-[1.125rem] items-center pl-2 text-sm font-semibold text-ink-subtle">
        {group.heading}
      </p>
      <ul className="space-y-0.5">
        {group.items.map((item) => (
          <li key={item.to}>
            <NavRow
              item={item}
              badge={navBadge(item.badge, counts)}
              leadBadge={navBadge(item.leadBadge, counts)}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One row of the open column. The rail draws its own, in `CollapsedRail`. */
function NavRow({
  item,
  badge,
  leadBadge = null,
  onNavigate,
}: {
  item: NavItem
  badge: NavBadge | null
  leadBadge?: NavBadge | null
  onNavigate?: () => void
}) {
  const { pathname } = useLocation()
  return (
    <NavLink
      to={item.to}
      end={rowEnd(item, pathname)}
      onClick={onNavigate}
      title={item.hint}
      className={({ isActive }) => cn(ROW, isActive ? 'bg-surface-selected text-ink-secondary hover:bg-surface-hover-strong' : ROW_IDLE)}
    >
      {({ isActive }) => (
        <>
          {/*
            20px, and an arbitrary value rather than `h-5` deliberately. Every
            glyph sizes ITSELF at `h-[1.15em]`, `cn` is a plain joiner, and
            Tailwind emits these two rules in alphabetical order, so `h-5` would
            sort BEFORE `h-[1.15em]` and lose to it. `h-[1.25rem]` sorts after
            and wins. Simplify this to `h-5` and the icons silently shrink.
          */}
          <item.icon className={cn('h-[1.25rem] w-[1.25rem]', isActive ? 'text-ink-secondary' : 'text-ink-subtle')} />
          <span className="truncate">{item.label}</span>
          {leadBadge === null ? null : <NavCount badge={leadBadge} className="ml-auto" />}
          {badge === null ? null : <NavCount badge={badge} className={cn(leadBadge === null && 'ml-auto')} />}
          {item.flag === undefined ? null : (
            <Badge tone="warning" className={cn(badge === null && leadBadge === null && 'ml-auto')}>
              {item.flag}
            </Badge>
          )}
        </>
      )}
    </NavLink>
  )
}

/**
 * One count on a row. The filled circle is the inbox rail's Action required
 * pill, measured the same, so the row and the rail read as one design.
 */
function NavCount({ badge, className }: { badge: NavBadge; className?: string }) {
  if (badge.tone === 'solid') {
    return (
      <span
        title={badge.title}
        className={cn(
          'inline-flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold tabular-nums text-ink-on',
          className,
        )}
      >
        {badge.text}
      </span>
    )
  }
  return (
    <Badge tone={badge.tone} title={badge.title} className={cn('tabular-nums', className)}>
      {badge.text}
    </Badge>
  )
}

/**
 * The card in the slot Salesforge spends on its trial: what this person may
 * still send today, and the two screens that explain it. It is the last thing
 * in the scrolling region, after the groups, which is where the reference
 * keeps its own card.
 *
 * The numbers are the server's, read off `/me/limits` through the same hook the
 * Safety controls screen uses, so this card and that screen share one cache
 * entry and cannot disagree. Nothing here is a number of its own. A person with
 * no allowance anywhere gets no card rather than an empty meter, and a request
 * that has not landed or was refused draws nothing either.
 */
function SendingAllowanceCard({ onNavigate }: { onNavigate?: () => void }) {
  const limits = useMyLimits()
  const allowance = sendingAllowance(limits.data?.items)
  if (allowance === null) return null

  return (
    <section
      aria-label="Sending allowance"
      className="mt-5 flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3"
    >
      <div>
        <h2 className="text-base font-semibold text-ink">Sending allowance</h2>
        <p className="text-xs text-ink-muted" data-numeric>
          <span className="font-semibold text-ink">{formatNumber(allowance.left)}</span> of{' '}
          <span className="font-semibold text-ink">{formatNumber(allowance.cap)}</span> sends left today, across every
          channel
        </p>
      </div>
      <MiniMeter value={allowance.used} max={allowance.cap} className="!w-full" />
      <div className="flex gap-2">
        <LinkButton to="/settings/safety?tab=mine" variant="primary" size="sm" onClick={onNavigate}>
          See usage
        </LinkButton>
        <LinkButton to="/settings/safety" variant="secondary" size="sm" onClick={onNavigate}>
          Limits
        </LinkButton>
      </div>
    </section>
  )
}
